import { Popover } from "@mui/material";
import { Plus } from "lucide-react";
import * as React from "react";

import { Button } from "@/shared/foundation/primitives/Button";
import { type Updater } from "@/shared/foundation/primitives/Table";
import { usePopupState } from "@/shared/hooks/usePopupState";

import type { FilterDefinition } from "../FilterDefinition";
import type { Filter, FilterMatchMode } from "../FilterSchema";
import { PropertyFilterMenu } from "../PropertyFilterMenu";

import styles from "./ActiveFilters.module.css";
import { FilterChip } from "./FilterChip";
import { MatchModePicker } from "./MatchModePicker";
import structuredStyles from "./StructuredActiveFilters.module.css";

export interface StructuredActiveFiltersProps {
  /** The structured filter list to render. Narrowed by the parent
   *  `<ActiveFilters>` switch — the variant component never sees
   *  `"none"` or `"roboql"` state. */
  filters: Filter[];
  /** AND/OR match mode. Picker is hidden when `filters.length < 2`. */
  matchMode: FilterMatchMode;
  filterDefinitions: FilterDefinition[];
  onRemove: (index: number) => void;
  onClear: () => void;
  onMatchModeChange: (updater: Updater<FilterMatchMode>) => void;
  /** Called when the `+` popover commits a property filter. The parent
   *  hook wraps the raw `Filter` into a `FilterMenuOutput` via
   *  `view.commit`. */
  onFilterSelected: (filter: Filter) => void;
}

/**
 * Chip-row variant rendered when `filters.type === "structured"`. Owns
 * the match-mode picker (2+ chips), the property chips, the inline `+`
 * button, and the `+` popover. The popover mounts `<PropertyFilterMenu>`
 * directly — no mode bar, no conflict banner. Different from
 * `<ResourceView.Filters>`'s trigger menu, which uses the outer
 * mode-switching `<FilterMenu>` shell when `roboql` is provided.
 *
 * The rationale: when the user already has structured chips in view,
 * the `+` is an "add another property filter" gesture. Switching to a
 * RoboQL editor here would be jarring — they'd reach for the main
 * Filter button instead. The chip-bar surfaces stay focused on the
 * mode that's actually active.
 */
export function StructuredActiveFilters({
  filters,
  matchMode,
  filterDefinitions,
  onRemove,
  onClear,
  onMatchModeChange,
  onFilterSelected,
}: StructuredActiveFiltersProps) {
  const addPopup = usePopupState();

  const handleAddClick = (event: React.MouseEvent<HTMLElement>) => {
    if (addPopup.isOpen) {
      addPopup.close();
    } else {
      addPopup.open({ anchorEl: event.currentTarget });
    }
  };

  return (
    <div className={styles.bar} role="region" aria-label="Active filters">
      {filters.length >= 2 && (
        <MatchModePicker mode={matchMode} onChange={onMatchModeChange} />
      )}
      {filters.map((filter, i) => {
        // Hydration with a definition list that doesn't include
        // ``filter.field`` (e.g. URL state restoring a filter for a
        // field whose definition has since been removed) still
        // renders an informative chip — ``fieldName`` falls back to
        // the raw field and ``options`` stays undefined so the
        // chip's value segment shows the wire value verbatim.
        const definition = filterDefinitions.find(
          (d) => d.field === filter.field && d.type === filter.type,
        );
        const options =
          (definition?.type === "set" || definition?.type === "enum") &&
          typeof definition.options !== "function"
            ? definition.options
            : undefined;
        return (
          <FilterChip
            key={`${filter.field}-${i}`}
            filter={filter}
            fieldName={definition?.name ?? filter.field}
            options={options}
            onRemove={() => onRemove(i)}
          />
        );
      })}
      <button
        type="button"
        className={structuredStyles.add}
        onClick={handleAddClick}
        aria-haspopup="dialog"
        aria-expanded={addPopup.isOpen}
        aria-label="Add filter"
      >
        <Plus />
      </button>
      <Popover
        open={addPopup.isOpen}
        anchorEl={addPopup.anchor}
        onClose={addPopup.close}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            className: styles.popover,
            role: "dialog",
            "aria-label": "Add property filter",
          },
        }}
      >
        <PropertyFilterMenu
          filterDefinitions={filterDefinitions}
          onCommit={(filter) => {
            onFilterSelected(filter);
            addPopup.close();
          }}
          onCancel={addPopup.close}
        />
      </Popover>
      <div className={styles.trailing}>
        <Button variant="text" size="small" color="inherit" onClick={onClear}>
          Clear
        </Button>
      </div>
    </div>
  );
}
